import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Instagram } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArtistDetailPage = () => {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtist();
  }, [artistId]);

  const fetchArtist = async () => {
    try {
      const response = await axios.get(`${API}/artists/${artistId}`);
      setArtist(response.data);
    } catch (error) {
      console.error('Error fetching artist:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="artist-detail-page-loading" className="tattoo-bg-home min-h-screen">
        <Header />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 min-h-screen flex items-center justify-center">
          <p className="font-body text-lg">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!artist) {
    return (
      <div data-testid="artist-not-found" className="tattoo-bg-home min-h-screen">
        <Header />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 min-h-screen flex items-center justify-center">
          <p className="font-heading text-3xl">Artist not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="artist-detail-page" className="tattoo-bg-home min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        
        {/* Hero Section */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="polaroid">
                <img
                  src={artist.image_url}
                  alt={artist.name}
                  className="w-full h-[600px] object-cover object-center"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="font-mono text-sm uppercase tracking-widest text-gray-600 mb-4">
                {artist.specialization}
              </p>
              <h1 className="font-heading text-6xl md:text-7xl font-bold mb-8 leading-none">
                {artist.name}
              </h1>
              <div className="paper-texture p-8 mb-8">
                <p className="font-body text-lg leading-relaxed">
                  {artist.bio}
                </p>
              </div>

              {artist.instagram_handle && (
                <a
                  href={`https://instagram.com/${artist.instagram_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="artist-instagram-link"
                  className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest hover:underline mb-8"
                >
                  <Instagram size={20} />
                  @{artist.instagram_handle}
                </a>
              )}

              <div className="mt-8">
                <a
                  href="/quote-estimator"
                  data-testid="book-with-artist-button"
                  className="inline-block bg-black text-white px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
                >
                  BOOK WITH {artist.name.split(' ')[0]} →
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Portfolio Grid */}
        {artist.portfolio_images && artist.portfolio_images.length > 0 && (
          <section>
            <div className="border-t-2 border-black pt-8 mb-12">
              <h2 className="font-heading text-5xl font-bold">PORTFOLIO</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {artist.portfolio_images.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="polaroid"
                >
                  <img
                    src={image}
                    alt={`${artist.name} portfolio ${index + 1}`}
                    className="w-full h-80 object-cover filter grayscale"
                  />
                </motion.div>
              ))}
            </div>
          </section>
        )}

      </div>

      <Footer />
    </div>
  );
};

export default ArtistDetailPage;
